'use client';

import { useQueryState, parseAsInteger } from 'nuqs';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { useUIStore } from '@/stores/ui-store';
import { SOURCE_LABELS } from '@/lib/constants';
import { useState, useEffect } from 'react';
import type { TrendFilters as TrendFiltersType } from '@/hooks/use-trends';

interface TrendFiltersProps {
  onFiltersChange: (filters: TrendFiltersType) => void;
}

const SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате' },
  { value: 'overall_score', label: 'По оценке' },
  { value: 'velocity_score', label: 'По скорости' },
];

const SOURCES = Object.keys(SOURCE_LABELS);

export function TrendFilters({ onFiltersChange }: TrendFiltersProps) {
  const [search, setSearch] = useQueryState('q');
  const [source, setSource] = useQueryState('source');
  const [minScore, setMinScore] = useQueryState('min_score', parseAsInteger);
  const [maxScore, setMaxScore] = useQueryState('max_score', parseAsInteger);
  const [lang, setLang] = useQueryState('lang');
  const [category, setCategory] = useQueryState('category');
  const [dateFrom, setDateFrom] = useQueryState('date_from');
  const [dateTo, setDateTo] = useQueryState('date_to');
  const [sortBy, setSortBy] = useQueryState('sort_by');
  const [order, setOrder] = useQueryState('order');

  const { filterPanelOpen, toggleFilterPanel } = useUIStore();

  const [searchInput, setSearchInput] = useState(search || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Sync debounced search to URL
  useEffect(() => {
    setSearch(debouncedSearch || null);
  }, [debouncedSearch, setSearch]);

  // Push all filter values to parent
  useEffect(() => {
    onFiltersChange({
      search: search || undefined,
      source: source || undefined,
      min_score: minScore ?? undefined,
      max_score: maxScore ?? undefined,
      language: lang || undefined,
      category: category || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort_by: sortBy || 'created_at',
      order: order || 'desc',
    });
  }, [search, source, minScore, maxScore, lang, category, dateFrom, dateTo, sortBy, order, onFiltersChange]);

  const resetFilters = () => {
    setSearch(null);
    setSource(null);
    setMinScore(null);
    setMaxScore(null);
    setLang(null);
    setCategory(null);
    setDateFrom(null);
    setDateTo(null);
    setSortBy(null);
    setOrder(null);
    setSearchInput('');
  };

  const hasActiveFilters = !!(search || source || minScore || maxScore || lang || category || dateFrom || dateTo);

  return (
    <div className="space-y-3">
      {/* Toolbar row */}
      <div className="flex items-center gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск трендов..."
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={toggleFilterPanel}
          className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm transition-colors ${
            filterPanelOpen
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-input bg-background text-muted-foreground hover:text-foreground'
          }`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span>Фильтры</span>
          {hasActiveFilters && (
            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              !
            </span>
          )}
        </button>

        {/* Sort dropdown */}
        <select
          value={sortBy || 'created_at'}
          onChange={(e) => setSortBy(e.target.value === 'created_at' ? null : e.target.value)}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Collapsible filter panel */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          filterPanelOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          {/* Source filter */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
              Источник
            </label>
            <div className="flex flex-wrap gap-1.5">
              {SOURCES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(source === s ? null : s)}
                  className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                    source === s
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {SOURCE_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Score range */}
          <div className="flex items-center gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
                Оценка от
              </label>
              <input
                type="number"
                min={1}
                max={5}
                value={minScore ?? ''}
                onChange={(e) => setMinScore(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="1"
                className="h-8 w-20 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:border-ring focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
                до
              </label>
              <input
                type="number"
                min={1}
                max={5}
                value={maxScore ?? ''}
                onChange={(e) => setMaxScore(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="5"
                className="h-8 w-20 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:border-ring focus:outline-none"
              />
            </div>
          </div>

          {/* Language toggle */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
              Язык
            </label>
            <div className="flex gap-1.5">
              {[
                { value: null, label: 'Все' },
                { value: 'ru', label: 'RU' },
                { value: 'en', label: 'EN' },
              ].map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setLang(opt.value)}
                  className={`rounded-md px-3 py-1 text-xs transition-colors ${
                    lang === opt.value || (!lang && opt.value === null)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
              Категория
            </label>
            <input
              type="text"
              value={category || ''}
              onChange={(e) => setCategory(e.target.value || null)}
              placeholder="Введите категорию..."
              className="h-8 w-full max-w-xs rounded-lg border border-input bg-background px-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
            />
          </div>

          {/* Date range */}
          <div className="flex items-center gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
                Дата от
              </label>
              <input
                type="date"
                value={dateFrom || ''}
                onChange={(e) => setDateFrom(e.target.value || null)}
                className="h-8 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:border-ring focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase">
                до
              </label>
              <input
                type="date"
                value={dateTo || ''}
                onChange={(e) => setDateTo(e.target.value || null)}
                className="h-8 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:border-ring focus:outline-none"
              />
            </div>
          </div>

          {/* Reset button */}
          <button
            onClick={resetFilters}
            className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            <span>Сбросить</span>
          </button>
        </div>
      </div>
    </div>
  );
}
